// Validation report card shown in Step 3 of the wizard + on the per-episode
// setup route once ffprobe completes. Pure render; the parent supplies the
// validation payload and the picked strategy via props.

import { Badge } from '@ohear/ui'
import type { EpisodeValidationReport } from '../../lib/api/producer-types'

export interface ValidationReportProps {
  report: EpisodeValidationReport
}

export function ValidationReport({ report }: ValidationReportProps) {
  const rows: Array<{ label: string; value: string; warn?: boolean }> = [
    {
      label: 'Frame rate',
      value: `${report.frame_rate_fps.toFixed(3)} fps${report.drop_frame ? ' (drop-frame)' : ''}`,
    },
    {
      label: 'Resolution',
      value: `${report.resolution_width} x ${report.resolution_height}`,
    },
    { label: 'Video codec', value: report.video_codec },
    {
      label: 'Audio',
      value: `${report.audio_sample_rate_hz / 1000} kHz, ${report.audio_channels}ch`,
      warn: report.audio_sample_rate_hz < 48_000,
    },
    {
      label: 'Duration',
      value: formatDuration(report.duration_sec),
    },
    {
      label: 'Script provided',
      value: report.has_script ? 'Yes' : 'No',
    },
    {
      label: 'SRT provided',
      value: report.has_srt ? 'Yes' : 'No',
    },
    {
      label: 'Estimated processing time',
      value: formatDuration(report.estimated_processing_sec),
    },
  ]

  return (
    <div className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg-2)]">
      <div className="border-b border-[var(--border)] px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-[14px] font-semibold text-[var(--text)] m-0">Validation report</h3>
          <Badge variant="green">Recommended: Strategy {report.recommended_strategy}</Badge>
        </div>
      </div>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1 px-4 py-3 sm:grid-cols-2">
        {rows.map((row, i) => (
          <div
            key={row.label}
            className="flex justify-between gap-3 py-1.5"
            style={{
              borderTop:
                i >= 2
                  ? '1px dashed color-mix(in srgb, var(--border) 60%, transparent)'
                  : undefined,
            }}
          >
            <dt className="text-[13px] text-[var(--text-2)]">{row.label}</dt>
            <dd className="text-[13px] font-medium text-[var(--text)] tabular-nums text-right">
              {row.warn ? <Badge variant="amber">{row.value}</Badge> : row.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  if (m < 60) return `${m}m ${s.toString().padStart(2, '0')}s`
  const h = Math.floor(m / 60)
  const mm = m % 60
  return `${h}h ${mm.toString().padStart(2, '0')}m`
}
